import { useState, useEffect } from "react";
import "../../../../../../styles/selectBar.css";

/**
 * ── 변경사항 (ECHO 톤) ──
 * 1. 테스트 6종마다 한 줄 설명 추가 ("영작 — 한글 보고 영어로 쓰기")
 * 2. "순서 고정" / "순서 랜덤" 소제목으로 그룹 분리
 * 3. 0세트 항목은 흐리게 — 제외 상태가 한눈에 보임
 * 4. 하단 힌트에 현재 조합 요약(총 세트 수) 실시간 표시
 * 5. 상태·updateTypes·기본값 초기화 로직은 기존 그대로
 */
const SentenceSelectionPage = ({
  worksheet,
  selectedTypes,
  setSelectedTypes,
}) => {
  const [blankCount, setBlankCount] = useState(0);
  const [scrambleCount, setScrambleCount] = useState(0);
  const [compositionCount, setCompositionCount] = useState(0);
  const [translationCount, setTranslationCount] = useState(0);
  const [dictationCount, setDictationCount] = useState(0);
  const [comprehensiveCount, setComprehensiveCount] = useState(0);

  const documents = worksheet.data?.documents || [];
  const sentenceList = worksheet.data?.sentence_list || [];

  const maxCounts = {
    blank: documents.filter((d) => d.type === "sentence:blank_root").length,
    scramble: documents.filter((d) => d.type === "sentence:scramble_root")
      .length,
    composition: sentenceList.length || 1,
    translation: sentenceList.length || 1,
    dictation: sentenceList.length || 1,
    comprehensive:
      documents.filter((d) => d.type === "sentence:all_root").length || 1,
  };

  const updateTypes = () => {
    const types = [];
    if (blankCount > 0) types.push({ key: "blank", count: blankCount });
    if (scrambleCount > 0)
      types.push({ key: "scramble", count: scrambleCount });
    if (compositionCount > 0)
      types.push({ key: "composition", count: compositionCount });
    if (translationCount > 0)
      types.push({ key: "translation", count: translationCount });
    if (dictationCount > 0)
      types.push({ key: "dictation", count: dictationCount });
    if (comprehensiveCount > 0)
      types.push({ key: "comprehensive", count: comprehensiveCount });

    setSelectedTypes(types);
  };

  useEffect(() => {
    updateTypes();
    // eslint-disable-next-line
  }, [
    blankCount,
    scrambleCount,
    compositionCount,
    translationCount,
    dictationCount,
    comprehensiveCount,
  ]);

  // 기본값 초기화 (기존 그대로)
  useEffect(() => {
    if (setSelectedTypes !== undefined && selectedTypes.length === 0) {
      setBlankCount(1);
      setScrambleCount(0);
      setCompositionCount(1);
      setTranslationCount(1);
      setDictationCount(0);
      setComprehensiveCount(1);
    }
    // eslint-disable-next-line
  }, []);

  // 화면 구성용 정의 (라벨 · 설명 · 상태 바인딩)
  const fixedRows = [
    {
      key: "blank",
      name: "빈칸 채우기",
      desc: "핵심 단어를 빈칸으로",
      value: blankCount,
      set: setBlankCount,
      max: maxCounts.blank,
    },
    {
      key: "scramble",
      name: "스크램블",
      desc: "어순 재배열",
      value: scrambleCount,
      set: setScrambleCount,
      max: maxCounts.scramble,
    },
    {
      key: "composition",
      name: "영작",
      desc: "한글 보고 영어로 쓰기",
      value: compositionCount,
      set: setCompositionCount,
      max: maxCounts.composition,
    },
    {
      key: "translation",
      name: "해석",
      desc: "영어 보고 한글로 쓰기",
      value: translationCount,
      set: setTranslationCount,
      max: maxCounts.translation,
    },
    {
      key: "dictation",
      name: "받아쓰기",
      desc: "듣고 문장 쓰기",
      value: dictationCount,
      set: setDictationCount,
      max: maxCounts.dictation,
    },
  ];
  const randomRows = [
    {
      key: "comprehensive",
      name: "종합",
      desc: "위 유형을 섞어 랜덤 출제",
      value: comprehensiveCount,
      set: setComprehensiveCount,
      max: maxCounts.comprehensive,
    },
  ];

  const totalSets =
    blankCount +
    scrambleCount +
    compositionCount +
    translationCount +
    dictationCount +
    comprehensiveCount;

  const includedNames = [...fixedRows, ...randomRows]
    .filter((r) => r.value > 0)
    .map((r) => `${r.name} ${r.value}`)
    .join(" · ");

  return (
    <div style={{ padding: "22px" }}>
      <div className="ws-modal-title">테스트 세트 설정</div>
      <p className="ws-modal-sub" style={{ marginBottom: "14px" }}>
        포함할 테스트와 세트 수를 정하세요. 0세트는 출제에서 빠집니다.
      </p>

      <div style={groupLabel}>순서 고정 테스트</div>
      {fixedRows.map((row) => (
        <SetRow key={row.key} row={row} />
      ))}

      <div style={{ ...groupLabel, marginTop: "16px" }}>순서 랜덤 테스트</div>
      {randomRows.map((row) => (
        <SetRow key={row.key} row={row} />
      ))}

      <div className="ws-hint" style={{ marginTop: "14px" }}>
        {totalSets > 0 ? (
          <>
            현재 구성: {includedNames} — <b>총 {totalSets}세트</b>
          </>
        ) : (
          <>선택된 테스트가 없습니다. 최소 1개 세트를 포함해주세요.</>
        )}
      </div>
    </div>
  );
};

/* ── 한 줄(테스트 항목) ── */
const SetRow = ({ row }) => {
  const dim = row.value === 0;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "8px 2px",
        borderBottom: "1px solid #F0F4F4",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            fontSize: "0.88rem",
            fontWeight: dim ? 500 : 800,
            color: dim ? "#C6CDD4" : "#0E141B",
            letterSpacing: "-0.01em",
          }}
        >
          {row.name}
        </span>
        <span
          style={{
            fontSize: "0.74rem",
            color: dim ? "#D6DEE0" : "#7A8490",
            marginLeft: "8px",
          }}
        >
          {row.desc}
        </span>
      </div>
      <select
        value={row.value}
        onChange={(e) => row.set(Number(e.target.value))}
        className="customSelect"
        style={{
          width: "92px",
          fontSize: "0.84rem",
          padding: "7px 10px",
          border: "1.5px solid #E6EDEC",
          borderRadius: "10px",
          fontFamily: "inherit",
          backgroundColor: "#fff",
          color: dim ? "#C6CDD4" : "#0E141B",
          cursor: "pointer",
        }}
      >
        {[...Array(row.max + 1)].map((_, i) => (
          <option key={i} value={i}>
            {i}세트
          </option>
        ))}
      </select>
    </div>
  );
};

const groupLabel = {
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: "#00A396",
  marginBottom: "4px",
};

export default SentenceSelectionPage;
